import EditableShape from '@recogito/annotorious/src/tools/EditableShape';
import { SVG_NAMESPACE, addClass, hasClass, removeClass } from '@recogito/annotorious/src/util/SVG';
import { format, setFormatterElSize } from '@recogito/annotorious/src/util/Formatting';
import { svgFragmentToShape } from '@recogito/annotorious/src/selectors/EmbeddedSVG';
import { pointInPolygon } from '@recogito/annotorious/src/util/Geom2D';
import './EditableMultipolygon.scss';
import { toSVGTarget, getPath } from './RubberbandMultipolygonTool';

const getPointsFromPathValue = polygon => {
  var results =polygon.split('M');
  var allcoords = []
  results.forEach(function (result, index) {
    if (result.length>0){
      let coords = []
      result=result.replace(/ Z/g,"Z")
      result=result.replace(/Z /g,"Z")
      result=result.replace(/Z/g,"")
      result=result.replace(/L /g,"L")
      result=result.replace(/ L/g,"L")
      var coordsString = result.split("L")
      coordsString.forEach(function(coord, index){
        coords.push([parseFloat(coord.split(",")[0]).toFixed(2).toString(),parseFloat(coord.split(",")[1]).toFixed(2).toString()]);
      });
      if (coords[0][0] !== coords[coords.length - 1][0] && coords[0][1] !== coords[coords.length - 1][1]){
        coords.push(coords[0])
      }
      allcoords.push(coords)
    }
  });
  return allcoords
}

const getPoints = (shape) => {
  const pointLists = getPointsFromPathValue(shape.querySelector('.a9s-inner').attributes.d.nodeValue)
  const pointArray = [];
  for (let pointList of pointLists) {
    let points = []
    for (let point of pointList) {
      let p = {
        x:parseFloat(point[0]),
        y:parseFloat(point[1])
      }
      points.push(p);
    }
    pointArray.push(points)
  }
  return pointArray;
}

const getBBox = shape => {
  return shape.querySelector('.a9s-inner').getBBox();
}

export const svgFragmentToPoints = annotation => {
  const svgShape = svgFragmentToShape(annotation);
  var polygon = svgShape.getAttribute('d')
  var allcoords =  getPointsFromPathValue(polygon)
  return allcoords
}

const drawEmbeddedSVG = annotation => {
  const shape = svgFragmentToShape(annotation);
  svgFragmentToPoints(annotation);

  const g = document.createElementNS(SVG_NAMESPACE, 'g');

  const inner = shape.cloneNode(true);
  inner.setAttribute('class', 'a9s-inner');

  const outer = shape.cloneNode(true);
  outer.setAttribute('class', 'a9s-outer');

  g.appendChild(outer);
  g.appendChild(inner);
  return g;
}

export default class EditableMultipolygon extends EditableShape {

  constructor(annotation, g, config, env) {
    super(annotation, g, config, env);
    this.svg.addEventListener('mousedown', this.onMouseDown, true);
    this.svg.addEventListener('mousemove', this.onMouseMove);
    this.svg.addEventListener('mouseup', this.onMouseUp);
    this.svg.addEventListener('contextmenu', this.onContextMenu, true);
    document.addEventListener('mousedown', this.onDocumentMouseDown, true);
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.addEventListener('contextmenu', this.onDocumentContextMenu, true);
    document.body.addEventListener('keydown', this.onKeyDown);
    if (!this.annotation.underlying["generated"]){
      console.log("generated", this.annotation);
      this.annotation.underlying["generated"] = Date.now()
      this.annotation.underlying["started"] = this.annotation.started
    }

    // Container wraps the mask + editable shape
    this.container = document.createElementNS(SVG_NAMESPACE, 'g');

    // The editable shape group
    this.shape = drawEmbeddedSVG(annotation);
    this.shape.setAttribute('class', 'improved-polygon');
    this.elementGroup = document.createElementNS(SVG_NAMESPACE, 'g');
    this.elementGroup.setAttribute('class', 'a9s-annotation editable selected');
    this.elementGroup.appendChild(this.shape);

    let cornerList = getPoints(this.shape);
    this.cornerHandles = []
    this.midpoints = []

    for (let corners of cornerList){
      let midpoints = []
      this.cornerHandles.push(corners.map((pt, idx) => {
        midpoints.push(this.createMidpoint(corners, idx));
        const handle = this.createCornerHandle(pt);
        return handle;
      }))
      this.midpoints.push(midpoints);
    }

    this.container.appendChild(this.elementGroup)
    g.appendChild(this.container);

    format(this.shape, annotation, config.formatter);

    this.shape.querySelector('.a9s-inner')
      .addEventListener('mousedown', this.onGrab(this.shape));

    this.grabbedElement = null;
    this.grabbedAt = null;
    this.selected = [];

    this.lastMouseDown = null;
    this.selectionBox = null;
    this.selectionStart = null;
  }

  // 🔥 UPDATE: Handles können jetzt den Fokus und Keydown-Events empfangen
  createCornerHandle = pt => {
    const handle = this.drawHandle(pt.x, pt.y);

    handle.setAttribute('tabindex', '-1');
    handle.style.outline = 'none';

    handle.addEventListener('mousedown', this.onGrab(handle));
    handle.addEventListener('click', this.onSelectCorner(handle));

    handle.addEventListener('keydown', (evt) => {
      if (evt.key === 'd' || evt.key === 'Delete') {
        evt.preventDefault();
        this.deleteSelected();
      }
    });

    this.scaleHandle(handle);

    this.elementGroup.appendChild(handle);
    return handle;
  }

  createMidpoint = (corners, idx) => {
    const thisCorner = corners[idx];
    const nextCorner = idx === corners.length - 1 ? corners[0] : corners[idx + 1];
    const x = (thisCorner.x + nextCorner.x) / 2;
    const y = (thisCorner.y + nextCorner.y) / 2;
    const handle = this.drawMidpoint(x, y);
    handle.addEventListener('mousedown', this.onGrab(handle));

    this.shape.appendChild(handle);
    return handle;
  }

  // 🔥 UPDATE: Kompletter Rebuild nach dem Löschen (verhindert Index-Fehler)
  deleteSelected = () => {
    if (this.selected.length === 0) return;

    // 1. Daten bereinigen
    const allPolygonPoints = getPoints(this.shape);
    let newPolygonPoints = [];

    allPolygonPoints.forEach((poly, polyIdx) => {
        let newPoly = poly.filter((pt, ptIdx) => {
            return !this.selected.some(sel => sel[0] === polyIdx && sel[1] === ptIdx);
        });

        // Nur behalten, wenn mind. 3 Punkte für eine Fläche existieren
        if (newPoly.length > 2) {
            newPolygonPoints.push(newPoly);
        }
    });

    // 2. ALTES UI AUFRÄUMEN
    this.cornerHandles.forEach(list => list.forEach(h => h.remove()));
    this.midpoints.forEach(list => list.forEach(h => h.remove()));

    this.cornerHandles = [];
    this.midpoints = [];
    this.selected = [];

    // 3. NEUES UI AUFBAUEN
    newPolygonPoints.forEach((poly) => {
        const handles = poly.map(pt => this.createCornerHandle(pt));
        this.cornerHandles.push(handles);

        const mids = poly.map((pt, idx) => this.createMidpoint(poly, idx));
        this.midpoints.push(mids);
    });

    // 4. PFAD MANUELL UPDATEN
    const str = getPath(newPolygonPoints);
    const inner = this.shape.querySelector('.a9s-inner');
    const outer = this.shape.querySelector('.a9s-outer');

    inner.setAttribute('d', str);
    outer.setAttribute('d', str);

    const { x, y, width, height } = outer.getBBox();
    setFormatterElSize(this.shape, x, y, width, height);

    // 5. Annotorious über das Update informieren
    this.emit('update', toSVGTarget(newPolygonPoints, this.env.image));
  }

  deselectCorners = () =>
    this.cornerHandles.forEach(h => removeClass(h, 'selected'));

  destroy = () => {
    this.container.parentNode.removeChild(this.container);

    this.svg.removeEventListener('mousedown', this.onMouseDown, true);
    this.svg.removeEventListener('mousemove', this.onMouseMove);
    this.svg.removeEventListener('mouseup', this.onMouseUp);
    this.svg.removeEventListener('contextmenu', this.onContextMenu, true);
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.removeEventListener('mousemove', this.onDocumentMouseMove, true);
    document.removeEventListener('mouseup', this.onDocumentMouseUp, true);
    document.removeEventListener('pointermove', this.onDocumentPointerMove, true);
    document.removeEventListener('pointerup', this.onDocumentPointerUp, true);
    document.removeEventListener('contextmenu', this.onDocumentContextMenu, true);

    document.body.removeEventListener('keydown', this.onKeyDown);

    super.destroy();
  }

  drawMidpoint = (x, y) => {
    const handle = document.createElementNS(SVG_NAMESPACE, 'circle');
    handle.setAttribute('class', 'a9s-midpoint');

    handle.setAttribute('cx', x);
    handle.setAttribute('cy', y);
    handle.setAttribute('r', 5 * this.scale);

    return handle;
  }

  get element() {
    return this.elementGroup;
  }

  onAddPoint = pos => {
    const corners = getPoints(this.shape);
    let idx = -1
    let midpointsIDX = -1
    for (let midpointList of this.midpoints){
      midpointsIDX += 1
      idx = midpointList.indexOf(this.grabbedElement);
      if (idx > -1){
        break;
      }
    }

    let updatedCorners = []
    let updatedcornerListIdx = -1
    let midBefore = null
    let midAfter = null

    for (let cornerList of corners){
      updatedcornerListIdx +=1
      if (updatedcornerListIdx === midpointsIDX){
        let updatedCornersList = []
        let updatedcornerIdx = -1
        for (let corner of cornerList){
          updatedcornerIdx += 1
          updatedCornersList.push(corner)
          if (updatedcornerIdx === idx){
            updatedCornersList.push(pos)
            midBefore = this.createMidpoint(cornerList, updatedcornerIdx -1);
            midAfter = this.createMidpoint(cornerList, updatedcornerIdx);
          }
        }
        updatedCorners.push(updatedCornersList)
      } else {
        updatedCorners.push(cornerList)
      }
    }

    const cornerHandle = this.createCornerHandle(pos);
    this.cornerHandles[midpointsIDX] = [
      ...this.cornerHandles[midpointsIDX].slice(0, idx+1),
      cornerHandle,
      ...this.cornerHandles[midpointsIDX].slice(idx+1)
    ];
    this.midpoints[midpointsIDX] = [
      ...this.midpoints[midpointsIDX].slice(0, idx),
      midBefore,
      midAfter,
      ...this.midpoints[midpointsIDX].slice(idx + 1)
    ];

    this.grabbedElement.parentNode.removeChild(this.grabbedElement);
    this.grabbedElement = cornerHandle;

    this.setPoints(updatedCorners);
  }

  // 🔥 UPDATE: Zwingt den Browser dazu, den Fokus auf das Element zu setzen
  onGrab = element => evt => {
    if (evt.button !== 0) return;  // left click

    if (evt.shiftKey && element === this.shape) {
      this.startBoxSelection(evt);
      return;
    }

    evt.stopPropagation();

    this.grabbedElement = element;
    this.grabbedAt = this.getSVGPoint(evt);
    this.lastMouseDown = new Date().getTime();

    if (element && typeof element.focus === 'function') {
        element.focus();
    }
  }

  // 🔥 UPDATE: Geschützt gegen Eingabefelder und 'd' hinzugefügt
  onKeyDown = (evt) => {
    if (evt.target.tagName === 'INPUT' || evt.target.tagName === 'TEXTAREA') return;

    if (evt.which === 46 || evt.which === 88 || evt.key === 'd') {
      // Löscht nur, wenn die Annotation gerade angewählt ist
      if (hasClass(this.shape, 'selected') || this.selected.length > 0) {
          evt.preventDefault();
          this.deleteSelected();
      }
    }
  }

  onMouseDown = evt => {
    const isBoxSelection = evt.button === 2 || (evt.button === 0 && evt.shiftKey);
    if (!isBoxSelection) return;
    if (evt.target.closest?.('.a9s-handle, .a9s-midpoint')) return;
    if (evt.button === 0 && evt.target.closest?.('.a9s-inner') && !evt.shiftKey) return;

    this.startBoxSelection(evt);
  }

  onDocumentMouseDown = evt => {
    const isBoxSelection = evt.button === 2 || (evt.button === 0 && evt.shiftKey);
    if (!isBoxSelection || this.selectionBox) return;
    if (evt.target.closest?.('.a9s-handle, .a9s-midpoint')) return;

    const bounds = this.svg.getBoundingClientRect();
    if (
      evt.clientX < bounds.left ||
      evt.clientY < bounds.top ||
      evt.clientX > bounds.right ||
      evt.clientY > bounds.bottom
    ) return;

    this.startBoxSelection(evt);
  }

  onDocumentPointerDown = evt => {
    const isBoxSelection = evt.button === 2 || (evt.button === 0 && evt.shiftKey);
    if (!isBoxSelection || this.selectionBox || !this.isInsideSVG(evt)) return;
    if (evt.target.closest?.('.a9s-handle, .a9s-midpoint')) return;

    this.startBoxSelection(evt);
  }

  onContextMenu = evt => {
    if (this.selectionBox || evt.button === 2) {
      this.stopOSDDrag(evt);
    }
  }

  onDocumentContextMenu = evt => {
    if (this.selectionBox || this.isInsideSVG(evt)) {
      this.stopOSDDrag(evt);
    }
  }

  isInsideSVG = evt => {
    const bounds = this.svg.getBoundingClientRect();
    return (
      evt.clientX >= bounds.left &&
      evt.clientY >= bounds.top &&
      evt.clientX <= bounds.right &&
      evt.clientY <= bounds.bottom
    );
  }

  stopOSDDrag = evt => {
    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation?.();
  }

  getEventSVGPoint = evt => {
    const pt = this.svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    return pt.matrixTransform(this.g.getScreenCTM().inverse());
  }

  startBoxSelection = evt => {
    this.stopOSDDrag(evt);

    this.grabbedElement = null;
    this.grabbedAt = null;
    this.selectionStart = this.getEventSVGPoint(evt);

    this.selectionBox = document.createElementNS(SVG_NAMESPACE, 'rect');
    this.selectionBox.setAttribute('class', 'a9s-selection-box');
    this.selectionBox.setAttribute('x', this.selectionStart.x);
    this.selectionBox.setAttribute('y', this.selectionStart.y);
    this.selectionBox.setAttribute('width', 0);
    this.selectionBox.setAttribute('height', 0);

    this.elementGroup.appendChild(this.selectionBox);

    document.addEventListener('mousemove', this.onDocumentMouseMove, true);
    document.addEventListener('mouseup', this.onDocumentMouseUp, true);
    document.addEventListener('pointermove', this.onDocumentPointerMove, true);
    document.addEventListener('pointerup', this.onDocumentPointerUp, true);
  }

  updateBoxSelection = pos => {
    const x = Math.min(this.selectionStart.x, pos.x);
    const y = Math.min(this.selectionStart.y, pos.y);
    const width = Math.abs(pos.x - this.selectionStart.x);
    const height = Math.abs(pos.y - this.selectionStart.y);

    this.selectionBox.setAttribute('x', x);
    this.selectionBox.setAttribute('y', y);
    this.selectionBox.setAttribute('width', width);
    this.selectionBox.setAttribute('height', height);

    const selected = [];

    this.cornerHandles.forEach((handles, cornerIdx) => {
      handles.forEach((handle, idx) => {
        const pt = this.getHandleXY(handle);

        if (
          pt.x >= x &&
          pt.y >= y &&
          pt.x <= x + width &&
          pt.y <= y + height
        ) {
          selected.push([cornerIdx, idx]);
        }
      });
    });

    this.selected = selected;
    this.setPoints(getPoints(this.shape));
  }

  finishBoxSelection = () => {
    if (this.selectionBox?.parentNode) {
      this.selectionBox.parentNode.removeChild(this.selectionBox);
    }

    this.selectionBox = null;
    this.selectionStart = null;

    document.removeEventListener('mousemove', this.onDocumentMouseMove, true);
    document.removeEventListener('mouseup', this.onDocumentMouseUp, true);
    document.removeEventListener('pointermove', this.onDocumentPointerMove, true);
    document.removeEventListener('pointerup', this.onDocumentPointerUp, true);
  }

  onDocumentMouseMove = evt => {
    if (!this.selectionBox) return;
    this.stopOSDDrag(evt);
    this.updateBoxSelection(this.getEventSVGPoint(evt));
  }

  onDocumentMouseUp = evt => {
    if (!this.selectionBox) return;
    this.stopOSDDrag(evt);
    this.finishBoxSelection();
  }

  onDocumentPointerMove = evt => {
    if (!this.selectionBox) return;
    this.stopOSDDrag(evt);
    this.updateBoxSelection(this.getEventSVGPoint(evt));
  }

  onDocumentPointerUp = evt => {
    if (!this.selectionBox) return;
    this.stopOSDDrag(evt);
    this.finishBoxSelection();
  }

  onMoveShape = pos => {
    const constrain = (coord, delta, max) =>
      coord + delta < 0 ? -coord : (coord + delta > max ? max - coord : delta);
    const { x, y, width, height } = getBBox(this.shape);
    const { naturalWidth, naturalHeight } = this.env.image;

    const dx = constrain(x, pos.x - this.grabbedAt.x, naturalWidth - width);
    const dy = constrain(y, pos.y - this.grabbedAt.y, naturalHeight - height);
    let updatedPoints = []

    for (let pointlist of getPoints(this.shape)){
      let newPointList = pointlist.map(pt => ([ pt.x , pt.y ]))
      if (pointInPolygon([this.grabbedAt.x,this.grabbedAt.y], newPointList)){
        updatedPoints.push(pointlist.map(pt =>
          ({ x: pt.x + dx, y: pt.y + dy })))
      } else {
        updatedPoints.push(pointlist)
      }
    }
    this.grabbedAt = pos;

    this.setPoints(updatedPoints);
  }

  onMoveCornerHandle = (pos, evt) => {
    let handleIdx = -1
    let cornerHandleIdx = 0
    for (let cornerHandle of this.cornerHandles){
      handleIdx = cornerHandle.indexOf(this.grabbedElement);
      if (handleIdx > -1){
        break
      } else {
        cornerHandleIdx += 1
      }
    }

    if (evt.ctrlKey) {
      this.selected = Array.from(new Set([...this.selected, handleIdx]));
    } else if (!this.selected.includes(handleIdx)) {
      this.selected = [ handleIdx ];
    }

    const points = getPoints(this.shape);
    const distances = this.selected.map(idx => {
      const handleXY = points[cornerHandleIdx][handleIdx];
      const thisXY = points[cornerHandleIdx][idx];
      return {
        index: idx,
        dx: thisXY.x - handleXY.x,
        dy: thisXY.y - handleXY.y
      }
    });

    let cornerHandleIdxUpdate = 0
    let updatedPoints = []

    for (let points of getPoints(this.shape)){
      if (cornerHandleIdxUpdate === cornerHandleIdx){
        let updatedPointList = points.map((pt, idx) => {
          if (idx === handleIdx) {
            return pos;
          } else if (this.selected.includes(idx)) {
            const { dx, dy } = distances.find(d => d.index === idx);
            return {
              x: pos.x + dx,
              y: pos.y + dy
            }
          } else {
            return pt;
          }
        });
        updatedPoints.push(updatedPointList);
      } else {
        updatedPoints.push(points)
      }
      cornerHandleIdxUpdate += 1
    }
    this.setPoints(updatedPoints);
  }

  onMouseMove = evt => {
    if (this.selectionBox) {
      this.stopOSDDrag(evt);
      this.updateBoxSelection(this.getEventSVGPoint(evt));
      return;
    }

    if (this.grabbedElement) {
      const pos = this.getSVGPoint(evt);
      if (this.grabbedElement === this.shape) {
        this.onMoveShape(pos);
      } else if (hasClass(this.grabbedElement, 'a9s-handle')) {
        this.onMoveCornerHandle(pos, evt);
      } else if (hasClass(this.grabbedElement, 'a9s-midpoint')) {
        this.onAddPoint(pos);
      }

      this.emit('update', toSVGTarget(getPoints(this.shape), this.env.image));
    }
  }

  onMouseUp = evt => {
    if (this.selectionBox) {
      this.stopOSDDrag(evt);
      this.finishBoxSelection();
      return;
    }

    this.grabbedElement = null;
    this.grabbedAt = null;
  }

  onScaleChanged = scale => {
    for (let cornerHandle of this.cornerHandles){
      cornerHandle.map(this.scaleHandle);
    }
    for (let midpoints of this.midpoints){
      midpoints.map(midpoint => {
        midpoint.setAttribute('r', 5 * this.scale);
      });
    }
  }

  // 🔥 UPDATE: Auch hier beim Klicken den Fokus garantieren
  onSelectCorner = handle => evt => {
    const isDrag = new Date().getTime() - this.lastMouseDown > 250;

    if (!isDrag) {
      let cornerhandlesIDX = -1
      let idx = -1
      for (let corners of this.cornerHandles){
        cornerhandlesIDX += 1
        idx = corners.indexOf(handle);
        if (idx > 0) break
      }

      if (evt?.ctrlKey) {
        if ( this.selected.map(object => (object[0] === cornerhandlesIDX && object[1] === idx)).indexOf(true) > -1) {
          this.selected = this.selected.filter(i => {
            return !(i[1] === idx && i[0] === cornerhandlesIDX)
          });
        } else
          this.selected = [...this.selected, [cornerhandlesIDX, idx]];
      } else {
        if (this.selected.length === 1 && (this.selected[0][1] === idx && this.selected[0][0] === cornerhandlesIDX)) {
          this.selected = [];
        } else {
          this.selected = [[cornerhandlesIDX, idx ]];
        }
      }

      this.setPoints(getPoints(this.shape));
    }

    if (handle && typeof handle.focus === 'function') {
      handle.focus();
    }
  }

  setPoints = points => {
    const str = getPath(points);
    const inner = this.shape.querySelector('.a9s-inner');
    inner.setAttribute('d', str);

    const outer = this.shape.querySelector('.a9s-outer');
    outer.setAttribute('d', str);

    let cornerIdx = 0
    for (let pointList of points){
      pointList.forEach((pt, idx) => {
        this.setHandleXY(this.cornerHandles[cornerIdx][idx], pt.x, pt.y)
      });

      for (let idx=0; idx<pointList.length; idx++) {
        const thisCorner = pointList[idx];
        const nextCorner = idx === pointList.length - 1 ? pointList[0] : pointList[idx + 1];
        const x = (thisCorner.x + nextCorner.x) / 2;
        const y = (thisCorner.y + nextCorner.y) / 2;

        const handle = this.midpoints[cornerIdx][idx];
        handle.setAttribute('cx', x);
        handle.setAttribute('cy', y);
      }
      cornerIdx +=1
    }

    let cornerHandleIdx = -1
    for (let corners of this.cornerHandles){
      cornerHandleIdx += 1

      corners.forEach((handle, i) => {
        const isSelected = this.selected.map(object => (object[0] === cornerHandleIdx && object[1] === i)).indexOf(true) > -1;
        if (isSelected && !hasClass(handle, 'selectedCorner')) {
          addClass(handle, 'selectedCorner');
        } else if (!isSelected && hasClass(handle, 'selectedCorner')) {
          removeClass(handle, 'selectedCorner');
        }
      });
    }

    const { x, y, width, height } = outer.getBBox();
    setFormatterElSize(this.shape, x, y, width, height);
  }

  updateState = annotation => {
    const shape = drawEmbeddedSVG(annotation);
    const points = getPoints(shape);
    this.setPoints(points);
  }
}